<script setup lang="ts">
import { computed } from 'vue'
import CanvasPreviewSurface, { type PreviewDrawItem, type PreviewHotspotItem } from '@/components/workspace/CanvasPreviewSurface.vue'
import ImageOptionPickerField from '@/components/ImageOptionPickerField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import type { SettingsReplayTabProps } from '@/features/workspace/settings/types'

const props = defineProps<SettingsReplayTabProps>()

const previewDrawItems = computed<PreviewDrawItem[]>(() => {
  const items: PreviewDrawItem[] = [
    {
      key: 'viewport',
      kind: 'rect',
      style: props.replayPreviewViewportStyle,
      fillColor: '#365882',
      strokeColor: '#6fb6ff',
      text: '回放内容区域',
      textColor: props.replayPreviewTextColor,
      fontFamily: props.replayPreviewFont,
      fontSize: props.replayPreviewFontSize,
      lineHeight: Math.max(1, Math.round(props.replayPreviewFontSize * 1.3)),
      padding: 6,
    },
  ]
  if (props.replayCloseButtonUrl) {
    items.push({
      key: 'close',
      kind: 'image',
      style: props.replayPreviewCloseStyle,
      imageUrl: props.replayCloseButtonUrl,
      placeholderText: '关闭按钮',
    })
  }
  return items
})

const previewHotspotItems = computed<PreviewHotspotItem[]>(() => {
  const items: PreviewHotspotItem[] = [
    {
      key: 'viewport',
      style: {
        ...props.replayPreviewViewportStyle,
        cursor: 'move',
      },
      ariaLabel: '回放区域拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startReplayViewportDrag(event),
      resizeHandle: {
        style: {
          position: 'absolute',
          right: '0',
          bottom: '0',
          width: '14px',
          height: '14px',
          borderLeft: '1px solid #8bc6ff',
          borderTop: '1px solid #8bc6ff',
          background: '#1f4f7f',
          cursor: 'nwse-resize',
        },
        ariaLabel: '调整回放区域大小',
        onPointerdown: (event: PointerEvent) => props.startReplayViewportResize(event),
      },
    },
  ]
  if (props.replayCloseButtonUrl) {
    items.push({
      key: 'close',
      style: {
        ...props.replayPreviewCloseStyle,
        cursor: 'move',
      },
      ariaLabel: '关闭按钮拖拽热区',
      onPointerdown: (event: PointerEvent) => props.startReplayCloseDrag(event),
    })
  }
  return items
})
</script>

<template>
  <div v-if="portraitLayout" class="settings-page settings-portrait-page replay-page-portrait">
    <section class="setting-card settings-preview-card settings-portrait-preview replay-preview-card">
      <CanvasPreviewSurface
        host-class="settings-preview-shell replay-preview-shell"
        stage-class="settings-preview-stage replay-preview-stage"
        :background-url="replayBackgroundUrl"
        :design-width="screenWidth"
        :design-height="screenHeight"
        background-empty-text="剧情回顾背景图"
        :draw-items="previewDrawItems"
        :hotspot-items="previewHotspotItems"
        :guide-active="previewGuideActive"
        @stage-resize="onPreviewStageResize"
      />
    </section>

    <div class="settings-portrait-props">
      <section class="setting-card replay-resource-card">
        <label class="field-block">
          <span class="field-label">背景图</span>
          <ResourceSelectField
            :model-value="replaySetting.BackPic.name"
            category="graphic"
            :base-folder-path="backgroundGraphicBaseFolderPath"
            display-mode="full"
            store-mode="relative"
            picker-title="选择剧情回顾背景图"
            @update:model-value="updateReplayBackPic"
          />
        </label>
      </section>

      <section class="setting-card">
        <div class="card-title">关闭按钮</div>

        <label class="field-block">
          <span class="field-label">图片选项索引</span>
          <ImageOptionPickerField
            :model-value="replaySetting.ButtonIndex"
            :items="imageOptionPickerItems"
            :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
            title="选择关闭按钮"
            @update:model-value="updateReplayButtonIndex"
          />
        </label>

        <label class="field-block">
          <span class="field-label">按钮位置</span>
          <div class="field-axis-grid field-axis-grid--plain">
            <NumberStepperField v-model="replaySetting.CloseEx.X" class="cell-input sm" />
            <NumberStepperField v-model="replaySetting.CloseEx.Y" class="cell-input sm" />
          </div>
        </label>
      </section>

      <section class="setting-card">
        <div class="card-title">回放内容区域</div>

        <label class="field-block">
          <span class="field-label">起始位置</span>
          <div class="field-axis-grid field-axis-grid--plain">
            <NumberStepperField v-model="replaySetting.Viewport.X" class="cell-input sm" />
            <NumberStepperField v-model="replaySetting.Viewport.Y" class="cell-input sm" />
          </div>
        </label>

        <label class="field-block">
          <span class="field-label">显示宽高</span>
          <div class="field-axis-grid field-axis-grid--plain">
            <NumberStepperField v-model="replaySetting.Viewport.Width" class="cell-input sm" :min="0" />
            <NumberStepperField v-model="replaySetting.Viewport.Height" class="cell-input sm" :min="0" />
          </div>
        </label>
      </section>
    </div>
  </div>

  <div v-else class="settings-page replay-page">
    <div class="replay-grid">
      <div class="replay-form-column">
        <section class="setting-card">
          <div class="card-title">关闭按钮</div>

          <label class="field-block">
            <span class="field-label">图片选项索引</span>
            <ImageOptionPickerField
              :model-value="replaySetting.ButtonIndex"
              :items="imageOptionPickerItems"
              :graphic-base-folder-path="imageOptionGraphicBaseFolderPath"
              title="选择关闭按钮"
              @update:model-value="updateReplayButtonIndex"
            />
          </label>

          <label class="field-block">
            <span class="field-label">按钮位置</span>
            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="replaySetting.CloseEx.X" class="cell-input sm" />
              <NumberStepperField v-model="replaySetting.CloseEx.Y" class="cell-input sm" />
            </div>
          </label>
        </section>

        <section class="setting-card">
          <div class="card-title">回放内容区域</div>

          <label class="field-block">
            <span class="field-label">起始位置</span>
            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="replaySetting.Viewport.X" class="cell-input sm" />
              <NumberStepperField v-model="replaySetting.Viewport.Y" class="cell-input sm" />
            </div>
          </label>

          <label class="field-block">
            <span class="field-label">显示宽高</span>
            <div class="field-axis-grid field-axis-grid--plain">
              <NumberStepperField v-model="replaySetting.Viewport.Width" class="cell-input sm" :min="0" />
              <NumberStepperField v-model="replaySetting.Viewport.Height" class="cell-input sm" :min="0" />
            </div>
          </label>
        </section>
      </div>

      <div class="replay-right-column">
        <section class="setting-card replay-resource-card">
          <label class="field-block">
            <span class="field-label">背景图</span>
            <ResourceSelectField
              :model-value="replaySetting.BackPic.name"
              category="graphic"
              :base-folder-path="backgroundGraphicBaseFolderPath"
              display-mode="full"
              store-mode="relative"
              picker-title="选择剧情回顾背景图"
              @update:model-value="updateReplayBackPic"
            />
          </label>
        </section>

        <section class="setting-card settings-preview-card replay-preview-card">
          <CanvasPreviewSurface
            host-class="settings-preview-shell replay-preview-shell"
            stage-class="settings-preview-stage replay-preview-stage"
            :background-url="replayBackgroundUrl"
            :design-width="screenWidth"
            :design-height="screenHeight"
            background-empty-text="剧情回顾背景图"
            :draw-items="previewDrawItems"
            :hotspot-items="previewHotspotItems"
            :guide-active="previewGuideActive"
            @stage-resize="onPreviewStageResize"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import './shared.css';

.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: 4px;
  box-sizing: border-box;
}

.replay-grid {
  display: grid;
  grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  gap: 8px;
  height: 100%;
  min-height: 0;
  align-items: start;
}

.replay-form-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.replay-right-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.replay-resource-card {
  flex-shrink: 0;
}

.replay-preview-card {
  grid-column: 2;
}

.replay-preview-viewport {
  position: absolute;
  border: 1px solid #6fb6ff;
  background: #365882;
  box-sizing: border-box;
  z-index: 1;
  cursor: move;
  user-select: none;
}

.replay-preview-viewport-text {
  padding: 6px;
  font-size: 11px;
  color: #ffffff;
}

.replay-preview-resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  border-left: 1px solid #8bc6ff;
  border-top: 1px solid #8bc6ff;
  background: #1f4f7f;
  cursor: nwse-resize;
}

.replay-preview-close {
  position: absolute;
  z-index: 2;
  overflow: hidden;
}

.replay-preview-close img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

@media (max-width: 980px) and (orientation: portrait) {
  .replay-grid {
    grid-template-columns: 1fr;
  }

  .replay-preview-card {
    grid-column: auto;
  }
}
</style>
